import React, { useEffect, useState } from 'react';
import './Toast.css';

/**
 * Toast Component
 * 
 * Displays dismissible notifications for non-blocking errors and messages
 * 
 * @param {String} type - Type of toast: 'success', 'warning', 'error', 'info'
 * @param {String} message - Message to display
 * @param {String} action - Optional action button text
 * @param {Function} onAction - Callback when action button is clicked
 * @param {Function} onDismiss - Callback when toast is dismissed
 * @param {Number} duration - Auto-dismiss duration in ms (0 = no auto-dismiss)
 * @param {Boolean} dismissible - Whether toast can be manually dismissed
 */
const Toast = ({ 
  type = 'info',
  message,
  action = null,
  onAction = null,
  onDismiss = null,
  duration = 5000,
  dismissible = true
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, duration);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => {
      setIsVisible(false);
      if (onDismiss) {
        onDismiss();
      }
    }, 300); // Match animation duration
  };

  const handleAction = () => {
    if (onAction) {
      onAction();
    }
    handleDismiss();
  };

  if (!isVisible) {
    return null;
  }

  const icons = {
    success: 'fa-check-circle',
    warning: 'fa-exclamation-triangle',
    error: 'fa-times-circle',
    info: 'fa-info-circle'
  };

  return (
    <div className={`toast toast-${type} ${isExiting ? 'toast-exit' : ''}`}>
      <div className="toast-icon">
        <i className={`fas ${icons[type]}`}></i>
      </div>
      <div className="toast-content">
        <p className="toast-message">{message}</p>
      </div>
      {action && onAction && (
        <button 
          className="toast-action"
          onClick={handleAction}
          type="button"
        >
          {action}
        </button>
      )}
      {dismissible && (
        <button 
          className="toast-dismiss"
          onClick={handleDismiss}
          type="button"
          aria-label="Dismiss"
        >
          <i className="fas fa-times"></i>
        </button>
      )}
    </div>
  );
};

/**
 * ToastContainer Component
 * 
 * Container for managing multiple toasts
 */
export const ToastContainer = ({ toasts = [], onRemove }) => {
  if (toasts.length === 0) {
    return null;
  }

  return (
    <div className="toast-container">
      {toasts.map((toast, index) => (
        <Toast
          key={toast.id || index}
          {...toast}
          onDismiss={() => onRemove && onRemove(toast.id || index)}
        />
      ))}
    </div>
  );
};

export default Toast;
